"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { bulkActionsService } from "@/services/bulkActions.service";

export interface ContextMenuActionOptions {
  propertyId: string;
  comment?: string;
}

export interface SharePropertyOptions {
  platform?: "email" | "whatsapp" | "telegram" | "clipboard";
  message?: string;
}

/**
 * Hook for managing context menu actions with proper error handling and loading states
 * Integrates with bulkActions.service.ts for API calls
 */
export const useContextMenuActions = () => {
  const queryClient = useQueryClient();

  // Actualize property mutation
  const actualizeProperty = useMutation({
    mutationFn: async (propertyId: string) => {
      const result = await bulkActionsService.actualize({
        propertyIds: [propertyId],
        updatePublicationDate: true,
        comment: "Actualized via context menu",
      });

      if (!result.success) {
        throw new Error(result.message || "Failed to actualize property");
      }

      return result;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property-table"] });

      // TODO: Show success toast
      console.log("Property actualized successfully:", data);
    },
    onError: (error) => {
      // TODO: Show error toast
      console.error("Failed to actualize property:", error);
    },
  });

  // Duplicate property mutation
  const duplicateProperty = useMutation({
    mutationFn: async (propertyId: string) => {
      // Note: This would need a separate API endpoint for duplicating
      // For now, we'll use a placeholder implementation
      const response = await fetch(`/api/properties/${propertyId}/duplicate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to duplicate property");
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property-table"] });

      // TODO: Show success toast
      console.log("Property duplicated successfully:", data);
    },
    onError: (error) => {
      // TODO: Show error toast
      console.error("Failed to duplicate property:", error);
    },
  });

  // Archive property mutation
  const archiveProperty = useMutation({
    mutationFn: async ({ propertyId, comment }: ContextMenuActionOptions) => {
      const result = await bulkActionsService.archive({
        propertyIds: [propertyId],
        comment: comment || "Archived via context menu",
      });

      if (!result.success) {
        throw new Error(result.message || "Failed to archive property");
      }

      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property-table"] });

      // TODO: Show success toast
      console.log("Property archived successfully:", data);
    },
    onError: (error) => {
      // TODO: Show error toast
      console.error("Failed to archive property:", error);
    },
  });

  // Delete property mutation (admin only)
  const deleteProperty = useMutation({
    mutationFn: async (propertyId: string) => {
      // This would be a separate DELETE API call
      const response = await fetch(`/api/properties/${propertyId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to delete property");
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["properties"] });
      queryClient.invalidateQueries({ queryKey: ["property-table"] });

      // TODO: Show success toast
      console.log("Property deleted successfully:", data);
    },
    onError: (error) => {
      // TODO: Show error toast
      console.error("Failed to delete property:", error);
    },
  });

  // Share property mutation
  const shareProperty = useMutation({
    mutationFn: async (propertyId: string, options?: SharePropertyOptions) => {
      // Generate shareable link or copy to clipboard
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}/properties/${propertyId}`;

      if (options?.platform === "clipboard" || !options?.platform) {
        await navigator.clipboard.writeText(shareUrl);
        return { success: true, url: shareUrl };
      }

      // Handle other sharing platforms
      switch (options.platform) {
        case "email":
          window.location.href = `mailto:?subject=Property Share&body=${shareUrl}`;
          break;
        case "whatsapp":
          window.open(`https://wa.me/?text=${encodeURIComponent(shareUrl)}`);
          break;
        case "telegram":
          window.open(
            `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`,
          );
          break;
      }

      return { success: true, url: shareUrl };
    },
    onSuccess: (data) => {
      // TODO: Show success toast
      console.log("Property shared successfully:", data);
    },
    onError: (error) => {
      // TODO: Show error toast
      console.error("Failed to share property:", error);
    },
  });

  return {
    // Mutations
    actualizeProperty,
    duplicateProperty,
    archiveProperty,
    deleteProperty,
    shareProperty,

    // Loading states
    isLoading:
      actualizeProperty.isPending ||
      duplicateProperty.isPending ||
      archiveProperty.isPending ||
      deleteProperty.isPending ||
      shareProperty.isPending,

    isActualizing: actualizeProperty.isPending,
    isDuplicating: duplicateProperty.isPending,
    isArchiving: archiveProperty.isPending,
    isDeleting: deleteProperty.isPending,
    isSharing: shareProperty.isPending,

    // Error states
    actualizeError: actualizeProperty.error,
    duplicateError: duplicateProperty.error,
    archiveError: archiveProperty.error,
    deleteError: deleteProperty.error,
    shareError: shareProperty.error,

    // Reset functions
    resetActualize: actualizeProperty.reset,
    resetDuplicate: duplicateProperty.reset,
    resetArchive: archiveProperty.reset,
    resetDelete: deleteProperty.reset,
    resetShare: shareProperty.reset,
  };
};
